import { datadogRum } from "@datadog/browser-rum";
import { useMutation } from "@tanstack/react-query";
import { z } from "zod";

import { apiRoutes } from "~/shared/services/api/api-routes.ts";
import { postAndValidate } from "~/shared/services/api/fetch-client.ts";

// The endpoint acks with a 200 and no meaningful body, so we don't constrain it.
const saveUserLoginEventsResponseSchema = z.unknown();

/**
 * Records a user login event by POSTing to `Admin/SaveUserLoginEvents` on the
 * LTL Quoting API. Mirrors the legacy Trax UI behavior of saving login events
 * once a logged-in external customer is resolved.
 *
 * This is a non-blocking, fire-and-forget background call: failures never
 * surface to the user or gate access. They are reported to Datadog RUM via
 * `onError` so they remain available for troubleshooting. Failed deliveries are
 * intentionally not retried — the session marker is already consumed, trading a
 * rare missed event for a guarantee of no duplicate rows (there is no end-to-end
 * idempotency key for the receiver to de-duplicate a retry).
 */
export const useSaveUserLoginEvents = () =>
	useMutation({
		mutationFn: async (): Promise<unknown> => {
			const result = await postAndValidate(
				apiRoutes.SAVEUSERLOGINEVENTS,
				{},
				saveUserLoginEventsResponseSchema,
			);
			if (!result.success) {
				throw new Error(result.error || "Failed to save user login events.");
			}
			return result.data;
		},
		onError: (error) => {
			datadogRum.addError(error, {
				feature: "user-login-events",
				route: apiRoutes.SAVEUSERLOGINEVENTS,
			});
		},
	});
